import React, { FC, useEffect, useRef, useState } from 'react';
import { Loader } from '@mantine/core';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { ProgressBar } from 'ui/ProgressBar';
import { InfoProgressPrice } from 'ui/InfoProgressPrice';
import { SummaryHistoryItem } from 'ui/SummaryHistoryItem';
import { Title } from 'components/Title';
import { currentYearNewFormat, getOnlyMounth, listMounth } from 'utils/date';
import { LoadingSkeleton } from 'components/LoadingSkeleton';
import styles from './Summary.module.scss';
import { NewSelectedMounth } from 'components/NewSelectedMounth';
import { SummaryHistoryItemMobile } from 'ui/SummaryHistoryItemMobile';
import { useGetTabQuery } from 'redux/Api/profileApi/ProfileApi';

const PER_PAGE = 50;
interface SummaryTypes {
  title: string;
}

interface AmountPaymentItemTypes {
  payment_type: string;
  amount: number;
}

export interface DataItemTypes {
  amount: number;
  description: string;
  id: number;
  incoming: boolean;
  name: string;
  order_link: string;
  paid_date: string;
  paid_time: string;
}

export const Summary: FC<SummaryTypes> = ({ title }) => {
  const [selectedMonth, setSelectedMonth] = useState(getOnlyMounth());
  const [selectedYear, setSelectedYear] = useState<number>(Number(currentYearNewFormat));
  const [page, setPage] = useState(1);
  const isFirstMonthRender = useRef(true);

  const { data, isLoading, isFetching } = useGetTabQuery({
    title,
    params: {
      year: selectedYear,
      month: listMounth.find((elem) => elem.name === selectedMonth)?.key,
      page,
      per_page: PER_PAGE,
    },
  });

  useEffect(() => {
    if (isFirstMonthRender.current) {
      isFirstMonthRender.current = false;
      return;
    }

    setPage(1);
  }, [selectedMonth, selectedYear]);

  const items = data?.data ?? [];
  const loadedCount = items.length;
  const totalCount = data?.count ?? 0;

  const hasNextPage =
    loadedCount > 0 &&
    (totalCount > loadedCount ||
      (loadedCount % PER_PAGE === 0 && (totalCount === 0 || totalCount <= loadedCount)));

  const [intersectionRef] = useInfiniteScroll({
    loading: isFetching,
    hasNextPage,
    onLoadMore: () => setPage((prev) => prev + 1),
    rootMargin: '0px 0px 160px 0px',
  });

  const amountsFiltered = data?.amount_by_payment_types?.filter(
    (price: AmountPaymentItemTypes) => price.payment_type !== 'Пополнение ЛК',
  );

  const showHistoryLoader = (isLoading || isFetching) && page === 1;

  return (
    <div>
      <div className={styles.progressBlock}>
        <div>
          <NewSelectedMounth
            total_amount={data?.total_amount}
            listMounth={listMounth}
            selectedMonth={listMounth?.find((elem: { name: string; key: number }) => {
              return elem.name === selectedMonth;
            })}
            setSelectedMonth={setSelectedMonth}
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
            showTotal_amount={true}
            loading={showHistoryLoader}
            setPage={setPage}
          />
        </div>
        <ProgressBar amounts={amountsFiltered} total_amount={data?.total_amount} />
        <div className={styles.payment_types}>
          {data?.amount_by_payment_types?.map(
            (price: AmountPaymentItemTypes, i: number) => (
              <InfoProgressPrice
                key={i}
                loading={showHistoryLoader}
                color={
                  price.payment_type === 'Выплата исполнителю'
                    ? 'orders'
                    : price.payment_type === 'Пополнение ЛК'
                    ? undefined
                    : 'taxes'
                }
                text={price.payment_type}
                price={price.amount}
              />
            ),
          )}
        </div>
      </div>
      <div className={styles.historyBlock}>
        <Title title='История' />
        {showHistoryLoader ? (
          <LoadingSkeleton fullWidth={true} />
        ) : (
          <>
            {loadedCount ? (
              items.map((elem, index) => (
                <SummaryHistoryItem item={elem} key={`${elem.id}-${index}`} />
              ))
            ) : (
              <p className={styles.nothing}>В этом месяце еще не было действий</p>
            )}
          </>
        )}
      </div>
      <div className={styles.historyBlockMobile}>
        <Title title='История' />
        {showHistoryLoader ? (
          <LoadingSkeleton fullWidth={true} />
        ) : (
          <>
            {loadedCount ? (
              items.map((elem, index) => (
                <SummaryHistoryItemMobile item={elem} key={`${elem.id}-${index}`} />
              ))
            ) : (
              <p className={styles.nothing}>В этом месяце еще не было действий</p>
            )}
          </>
        )}
      </div>
      {loadedCount > 0 && hasNextPage ? (
        <div style={{ height: '10px' }} ref={intersectionRef} />
      ) : null}
      {isFetching && page > 1 && loadedCount > 0 && (
        <div className={styles.loader}>
          <Loader size='xs' color='#002cfb' />
        </div>
      )}
    </div>
  );
};
